import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject, filter, map, take, takeUntil, timer } from 'rxjs';
import SignaturePad from 'signature_pad';

@Component({
  selector: 'app-c-drawing-canvas',
  templateUrl: './c-drawing-canvas.component.html',
  styleUrls: ['./c-drawing-canvas.component.scss'],
})
export class CDrawingCanvasComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ccontainer', { static: false }) cContainer!: ElementRef;
  @ViewChild('canvas', { static: true }) drawingPadElement!: ElementRef;
  @ViewChild('geometricCanvas', { static: true }) geometricPadElement!: ElementRef;
  @Input({ required: true }) resizedWindowTrigger$!: Subject<any>;
  @Input({ required: true }) initialDatas: string | undefined;
  @Input({ required: true }) overWriteSubject$!: Subject<string | null>;
  @Input({ required: true }) clearPadGeometric$!: Subject<any>;
  @Input({ required: true }) canDraw!: boolean;
  @Input({ required: true }) marqueurSize!: number;
  @Input({ required: true }) mode!: 'rectangle' | 'ellipse' | 'line' | 'drawing';
  @Output() triggerAlert = new EventEmitter<{ data: string }>();

  readonly pencolor= '#333333'
  readonly temporarypencolor= '#ff8085'

  dimensionX: number | undefined = 10;
  dimensionY: number | undefined = 10;

  // displayConfirmCancelButtons: boolean = false;

  destroyedComponent$ = new Subject();

  // éléments dessin
  drawingPad!: SignaturePad;
  eventListenerOrEventListenerObject: EventListenerOrEventListenerObject = () => this.updateValue();

  // éléments carré
  geometricPad!: SignaturePad;
  eventBeginGeometricEvent: EventListenerOrEventListenerObject = $event => this.beginGeometricShape($event);
  eventAfterUpdateGeometric: EventListenerOrEventListenerObject = $event => this.updateGeometricShape($event);
  eventEndGeometricEvent: EventListenerOrEventListenerObject = $event => this.endGeometricShape($event);
  startGeometricCoordinates: { x: number, y: number } | null = null;
  rectangleToApply: { x: number, y: number, width: number, height: number } | null = null;
  ellipseToApply: { x: number, y: number, radiusX: number, radiusY: number } | null = null;
  lineToApply: { x: number, y: number, endX: number, endY: number } | null = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.overWriteSubject$.pipe(
      takeUntil(this.destroyedComponent$)
    ).subscribe(_ => {
      if (typeof _ === 'string' && _.length > 0) {
        this.overrightValue(_);
      } else {
        this.clearValue();
      }
    });
    this.clearPadGeometric$.pipe(
      takeUntil(this.destroyedComponent$)
    ).subscribe(_ => this.cancelShape());

    this.drawingPad = new SignaturePad(this.drawingPadElement.nativeElement);
    if (this.canDraw) {
      this.drawingPad.penColor = this.pencolor;
      this.drawingPad.minWidth = 2;
      this.drawingPad.addEventListener('endStroke', this.eventListenerOrEventListenerObject);
    } else {
      this.drawingPad.penColor = 'transparent';
    }

    this.geometricPad = new SignaturePad(this.geometricPadElement.nativeElement);
    this.geometricPad.penColor = 'transparent';
    if (this.canDraw) {
      this.geometricPad.addEventListener('beginStroke', this.eventBeginGeometricEvent);
      this.geometricPad.addEventListener('afterUpdateStroke', this.eventAfterUpdateGeometric);
      this.geometricPad.addEventListener('endStroke', this.eventEndGeometricEvent);
    }
  }

  ngAfterViewInit() {
    timer(0, 100).pipe(
      map(_ => this.cContainer),
      filter(_ => _ !== undefined),
      map(_ => this.getDimensions(_)),
      filter(({width, height}) => typeof width === 'number' && width !== 0 && typeof height === 'number' && height !== 0),
      take(1),
      takeUntil(this.destroyedComponent$)
    ).subscribe(_ => {
      this.setCanvasDimensions();
      this.changeDetectorRef.detectChanges();
      this.applyInitialDatasOnCanvas();
        this.resizedWindowTrigger$.pipe(
        takeUntil(this.destroyedComponent$)
      ).subscribe(_ => {
        const data = this.drawingPad.toDataURL();
        this.setCanvasDimensions();
        setTimeout(() => {
          this.setCanvasNewDatas(data);
          this.changeDetectorRef.detectChanges();
        }, 100)
      })
    })
  }

  ngOnDestroy() {
    this.destroyedComponent$.next(true);
  }

  getDimensions(element: ElementRef) {
    const width = element.nativeElement.offsetWidth;
    const height = element.nativeElement.offsetHeight;
    return {width, height};
  }

  setCanvasDimensions() {
    const width = this.cContainer.nativeElement.offsetWidth;
    const height = this.cContainer.nativeElement.offsetHeight;
    this.dimensionX = width - this.marqueurSize;
    this.dimensionY = height - this.marqueurSize;
  }

  setCanvasNewDatas(data: string) {
    this.drawingPad.fromDataURL(data, {
      height: this.dimensionY,
      width: this.dimensionX,
    });
    this.changeDetectorRef.detectChanges();
  }

  updateValue() {
    const data = this.drawingPad.toDataURL();
    this.triggerAlert.emit({ data });
  }

  beginGeometricShape($event: any) {
    this.rectangleToApply = null;
    this.ellipseToApply = null;
    // this.displayConfirmCancelButtons = false;
    const x = this.startGeometricCoordinates?.x;
    const y = this.startGeometricCoordinates?.y;
    if (typeof x !== 'number' || typeof y !== 'number') {
      const layerX = $event?.detail?.event?.layerX;
      const layerY = $event?.detail?.event?.layerY;
      if (typeof layerX === 'number' && typeof layerY === 'number') {
        this.startGeometricCoordinates = {
          x: layerX,
          y: layerY
        }
      }
    }
  }

  updateGeometricShape($event: any) {
    if (this.mode === 'rectangle') {
      this.updateRectangle($event);
    }
    if (this.mode === 'ellipse') {
      this.updateEllipsis($event);
    }
    if (this.mode === 'line') {
      this.updateLine($event);
    }
  }

  endGeometricShape($event: any) {
    if (this.mode === 'rectangle') {
      this.endRectangle($event);
    }
    if (this.mode === 'ellipse') {
      this.endEllipsis($event);
    }
    if (this.mode === 'line') {
      this.endLine($event);
    }
    this.startGeometricCoordinates = null;
  }

  updateRectangle($event: any) {
    const x = this.startGeometricCoordinates?.x;
    const y = this.startGeometricCoordinates?.y;
    if (typeof x === 'number' && typeof y === 'number') {
      const layerX = $event?.detail?.event?.layerX;
      const layerY = $event?.detail?.event?.layerY;
      if (typeof layerX === 'number' && typeof layerY === 'number') {
        const ctx = this.geometricPadElement.nativeElement.getContext("2d");
        this.geometricPad.clear();
        if (layerX - x !== 0 && layerY - y !== 0) {
          this.rectangleToApply = {
            x,
            y,
            width: layerX - x,
            height: layerY - y
          };
          ctx.lineWidth = 4;
          ctx.strokeStyle = this.temporarypencolor;
          ctx.strokeRect(this.rectangleToApply.x, this.rectangleToApply.y, this.rectangleToApply.width, this.rectangleToApply.height);
        }
      }
    }
  }

  endRectangle($event: any) {
    if (this.rectangleToApply) {
      this.confirmShape();
      // this.displayConfirmCancelButtons = true;
    }
  }

  updateEllipsis($event: any) {
    const x = this.startGeometricCoordinates?.x;
    const y = this.startGeometricCoordinates?.y;
    if (typeof x === 'number' && typeof y === 'number') {
      const layerX = $event?.detail?.event?.layerX;
      const layerY = $event?.detail?.event?.layerY;
      if (typeof layerX === 'number' && typeof layerY === 'number') {
        const ctx = this.geometricPadElement.nativeElement.getContext("2d");
        this.geometricPad.clear();
        if (layerX - x !== 0 && layerY - y !== 0) {
          this.ellipseToApply = {
            x: (layerX + x) / 2,
            y: (layerY + y) / 2,
            radiusX: Math.abs(layerX - x) / 2,
            radiusY: Math.abs(layerY - y) / 2,
          };
          ctx.lineWidth = 4;
          ctx.strokeStyle = this.temporarypencolor;
          ctx.beginPath();
          ctx.ellipse(this.ellipseToApply.x, this.ellipseToApply.y, this.ellipseToApply.radiusX, this.ellipseToApply.radiusY, 0, 0, 2 * Math.PI);
          ctx.stroke();
        }
      }
    }
  }

  endEllipsis($event: any) {
    if (this.ellipseToApply) {
      this.confirmShape();
      // this.displayConfirmCancelButtons = true;
    }
  }

  updateLine($event: any) {
    const x = this.startGeometricCoordinates?.x;
    const y = this.startGeometricCoordinates?.y;
    if (typeof x === 'number' && typeof y === 'number') {
      const layerX = $event?.detail?.event?.layerX;
      const layerY = $event?.detail?.event?.layerY;
      if (typeof layerX === 'number' && typeof layerY === 'number') {
        const ctx = this.geometricPadElement.nativeElement.getContext("2d");
        this.geometricPad.clear();
        if (layerX - x !== 0 && layerY - y !== 0) {
          this.lineToApply = {
            x,
            y,
            endX: layerX,
            endY: layerY
          };
          ctx.lineWidth = 4;
          ctx.strokeStyle = this.temporarypencolor;
          ctx.beginPath();
          ctx.moveTo(this.lineToApply.x, this.lineToApply.y);
          ctx.lineTo(this.lineToApply.endX, this.lineToApply.endY);
          ctx.stroke();
        }
      }
    }
  }

  endLine($event: any) {
    if (this.lineToApply) {
      this.confirmShape();
      // this.displayConfirmCancelButtons = true;
    }
  }

  clearValue() {
    this.drawingPad.clear();
    this.triggerAlert.emit({ data: '' });
  }

  overrightValue(data: string) {
    this.drawingPad.clear();
    this.setCanvasNewDatas(data);
    this.changeDetectorRef.detectChanges();
    this.triggerAlert.emit({ data });
  }

  confirmShape() {
    const drawingctx = this.drawingPadElement.nativeElement.getContext("2d");
    if (this.mode === 'rectangle') {
      const x = this.rectangleToApply?.x
      const y = this.rectangleToApply?.y
      const width = this.rectangleToApply?.width
      const height = this.rectangleToApply?.height
      if (
        typeof x === 'number'
        && typeof y === 'number'
        && typeof width === 'number'
        && typeof height === 'number'
      ) {
        drawingctx.lineWidth = 4;
        drawingctx.strokeStyle = this.pencolor;
        drawingctx.strokeRect(x, y, width, height);
      }
    }
    if (this.mode === 'ellipse') {
      const x = this.ellipseToApply?.x;
      const y = this.ellipseToApply?.y;
      const radiusX = this.ellipseToApply?.radiusX;
      const radiusY = this.ellipseToApply?.radiusY;
      if (
        typeof x === 'number'
        && typeof y === 'number'
        && typeof radiusX === 'number'
        && typeof radiusY === 'number'
      ) {
        drawingctx.lineWidth = 4;
        drawingctx.strokeStyle = this.pencolor;
        drawingctx.beginPath();
        drawingctx.ellipse(x, y, radiusX, radiusY, 0, 0, 2 * Math.PI);
        drawingctx.stroke();
      }
    }
    if (this.mode === 'line') {
      const x = this.lineToApply?.x;
      const y = this.lineToApply?.y;
      const endX = this.lineToApply?.endX;
      const endY = this.lineToApply?.endY;
      if (
        typeof x === 'number'
        && typeof y === 'number'
        && typeof endX === 'number'
        && typeof endY === 'number'
      ) {
        drawingctx.lineWidth = 4;
        drawingctx.strokeStyle = this.pencolor;
        drawingctx.beginPath();
        drawingctx.moveTo(x, y);
        drawingctx.lineTo(endX, endY);
        drawingctx.stroke();
      }
    }
    if (this.mode === 'rectangle' || this.mode === 'ellipse' || this.mode === 'line') {
      this.updateValue();
      this.geometricPad.clear();
      this.rectangleToApply = null;
      this.ellipseToApply = null;
      this.lineToApply = null;
      // this.displayConfirmCancelButtons = false;
    }
  }

  cancelShape() {
    this.geometricPad.clear();
    this.rectangleToApply = null;
    this.ellipseToApply = null;
    // this.displayConfirmCancelButtons = false;
  }

  applyInitialDatasOnCanvas() {
    if (typeof this.initialDatas === 'string' && this.initialDatas.length > 0) {
      this.setCanvasNewDatas(this.initialDatas);
    }
  }

}
